import { useCallback } from 'react';

/**
 * useClipboard — copy text to clipboard with a fallback for older browsers.
 *
 * Usage:
 *   const { copy } = useClipboard({ onSuccess: (id) => setCopiedId(id) });
 *   copy('text to copy', 'my-button-id');
 *   copy('plain text', 'my-button-id', { html: '<h2>rich</h2>' });
 *
 * With `html` the clipboard receives BOTH `text/html` and `text/plain`, so a
 * rich editor (Word, Google Docs, Notion, a CMS visual editor) pastes headings,
 * bold and lists while a plain-text field still gets the text. Order of
 * attempts: ClipboardItem (both types) → writeText (plain only) → execCommand.
 * ⚠️ Rich copy needs a secure context AND user activation, exactly like
 * writeText; where ClipboardItem is missing (Firefox < 127) the plain text
 * still lands.
 */
export function useClipboard({ onSuccess } = {}) {
  const execCommandFallback = useCallback((text) => {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;';
      document.body.appendChild(el);
      el.select();
      el.setSelectionRange(0, el.value.length);
      let ok = false;
      try { ok = document.execCommand('copy'); } catch (_e) { ok = false; }
      document.body.removeChild(el);
      return ok;
    } catch (_e) { return false; }
  }, []);

  const copy = useCallback((text, id = 'default', { html } = {}) => {
    const succeed = () => onSuccess?.(id);
    const plain = text == null ? '' : String(text);

    const writePlain = () => {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(plain)
          .then(succeed)
          .catch(() => { execCommandFallback(plain) && succeed(); });
      } else {
        execCommandFallback(plain) && succeed();
      }
    };

    try {
      const canWriteRich =
        !!html && typeof window !== 'undefined' && typeof window.ClipboardItem === 'function' &&
        typeof navigator.clipboard?.write === 'function';
      if (canWriteRich) {
        const item = new window.ClipboardItem({
          'text/plain': new Blob([plain], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        });
        navigator.clipboard.write([item]).then(succeed).catch(writePlain);
      } else {
        writePlain();
      }
    } catch (_e) { /* silent */ }
  }, [execCommandFallback, onSuccess]);

  return { copy };
}
